import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/axios';
import toast from 'react-hot-toast';
import {
  UsersIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const AdminDashboard = () => {
  const [statistics, setStatistics] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        api.get('/admin/statistics'),
        api.get('/admin/users?limit=10'),
      ]);

      setStatistics(statsRes.data.statistics);
      setUsers(usersRes.data.users);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-orange">
            <ChartBarIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="page-title">Admin Dashboard</h1>
            <p className="text-sm text-neutral-500">System overview and management tools</p>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 stagger-children">
        <div className="stat-card group">
          <div className="flex items-start justify-between">
            <div className="stat-icon bg-blue-50 text-blue-500">
              <BuildingOfficeIcon className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-4">
            <p className="stat-value">{statistics?.tenants?.total || 0}</p>
            <p className="stat-label">Total Tenants</p>
          </div>
        </div>

        <div className="stat-card group">
          <div className="flex items-start justify-between">
            <div className="stat-icon bg-purple-50 text-purple-500">
              <UsersIcon className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-4">
            <p className="stat-value">{statistics?.users?.total || 0}</p>
            <p className="stat-label">Total Users</p>
          </div>
        </div>

        <div className="stat-card group">
          <div className="flex items-start justify-between">
            <div className="stat-icon bg-green-50 text-green-500">
              <CreditCardIcon className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-4">
            <p className="stat-value">{statistics?.subscriptions?.active || 0}</p>
            <p className="stat-label">Active Subscriptions</p>
          </div>
        </div>

        <div className="stat-card group">
          <div className="flex items-start justify-between">
            <div className="stat-icon bg-yellow-50 text-yellow-500">
              <CurrencyDollarIcon className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-4">
            <p className="stat-value">{formatCurrency(statistics?.payments?.totalRevenue || 0)}</p>
            <p className="stat-label">Total Revenue</p>
          </div>
        </div>

        <div className="stat-card group">
          <div className="flex items-start justify-between">
            <div className="stat-icon bg-primary-50 text-primary-500">
              <ChatBubbleLeftRightIcon className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-4">
            <p className="stat-value">{statistics?.workspaces?.active || 0}</p>
            <p className="stat-label">Active Workspaces</p>
          </div>
        </div>
      </div>

      {/* Recent Users */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-800">Recent Users</h3>
            <p className="text-sm text-neutral-500 mt-1">Latest registered users</p>
          </div>
          <Link to="/admin/users" className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors">
            View all
          </Link>
        </div>

        {users.length === 0 ? (
          <div className="text-center py-8">
            <UsersIcon className="mx-auto h-12 w-12 text-neutral-300" />
            <h3 className="mt-2 text-sm font-medium text-neutral-800">No users</h3>
            <p className="mt-1 text-sm text-neutral-500">No users have registered yet.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">User</th>
                  <th className="table-header-cell">Organization</th>
                  <th className="table-header-cell">Role</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Joined</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {users.map((u) => (
                  <tr key={u.id} className="table-row-hover">
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                          <span className="text-xs font-semibold text-white">
                            {u.firstName?.charAt(0)}{u.lastName?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-800">{u.firstName} {u.lastName}</p>
                          <p className="text-xs text-neutral-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell text-sm text-neutral-600">
                      {u.tenantName || '—'}
                    </td>
                    <td className="table-cell">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        u.role === 'owner' ? 'bg-primary-100 text-primary-800' :
                        u.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${
                        u.status === 'active' ? 'badge-green' :
                        u.status === 'pending' ? 'badge-yellow' : 'badge-red'
                      }`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="table-cell text-sm text-neutral-500">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Plan Distribution */}
      {statistics?.subscriptions?.distribution && (
        <div className="card">
          <h3 className="text-lg font-semibold text-neutral-800 mb-6">Plan Distribution</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(statistics.subscriptions.distribution).map(([planName, count]) => (
              <div key={planName} className="p-4 bg-neutral-50 rounded-lg border border-neutral-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-800 capitalize">{planName}</p>
                    <p className="text-2xl font-bold text-primary-600">{count}</p>
                  </div>
                  <CreditCardIcon className="w-8 h-8 text-neutral-300" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-neutral-800 mb-6">Quick Actions</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { to: '/admin/users', icon: UsersIcon, title: 'Manage Users', description: 'View and manage user accounts', color: 'blue' },
            { to: '/admin/tenants', icon: BuildingOfficeIcon, title: 'Manage Tenants', description: 'View tenant organizations', color: 'green' },
            { to: '/payments', icon: CurrencyDollarIcon, title: 'Payment Reports', description: 'View payment analytics', color: 'yellow' },
            { to: '/admin/settings', icon: ChartBarIcon, title: 'System Settings', description: 'Configure system options', color: 'purple' },
          ].map((action) => (
            <Link
              key={action.to}
              to={action.to}
              className="group relative p-5 rounded-xl border border-neutral-200 bg-white hover:border-primary-300 hover:shadow-md transition-all duration-300"
            >
              <div className={`w-12 h-12 rounded-xl bg-${action.color}-50 text-${action.color}-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <action.icon className="w-6 h-6" />
              </div>
              <h4 className="text-base font-semibold text-neutral-800 group-hover:text-primary-600 transition-colors">
                {action.title}
              </h4>
              <p className="mt-1 text-sm text-neutral-500">{action.description}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
